import { Anchor, Button, Collapse, Group, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type { WeeklyMetrics, WeeklyStaleTicket, WeeklyTicketStats } from "@msc/shared";

import { formatDauer } from "../../lib/weekly.js";
import { Datenstand } from "./Datenstand.js";

/**
 * Ticket-Kennzahlen: offene Tickets · Ø Antwortzeit · Ø Lösungsdauer, jeweils
 * mit `n`, plus die längsten offenen Tickets ohne Aktivität. Gesamt als
 * Kachelreihe mit Datenstand; `kompakt` als eine Zeile für die Kundenzeile,
 * die Top-Liste dort aufklappbar.
 */
export function TicketSummary({
  stats,
  firstResponseMode,
  jiraBrowseBaseUrl,
  jiraSyncedAt = null,
  bezugszeit = new Date(),
  kompakt = false,
}: {
  stats: WeeklyTicketStats;
  firstResponseMode: WeeklyMetrics["firstResponseMode"];
  jiraBrowseBaseUrl: string | null;
  jiraSyncedAt?: string | null;
  /** Bezug des Datenstand-Hinweises (nur Gesamtansicht). */
  bezugszeit?: Date;
  kompakt?: boolean;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(false);
  const listeId = useId();
  const antwortLabel = t(
    firstResponseMode === "sla" ? "weekly.metrics.avgFirstResponseSla" : "weekly.metrics.avgFirstResponseCalendar",
  );
  // Eine Kennzahl, eine Einheit: Antwortzeit immer in h/min, Lösungsdauer immer in Tagen.
  const antwort = formatDauer(t, stats.firstResponse.avgMs, "stunden");
  const loesung = formatDauer(t, stats.resolution.avgMs, "tage");
  const nAntwort = t("weekly.metrics.n", { n: stats.firstResponse.n });
  const nLoesung = t("weekly.metrics.n", { n: stats.resolution.n });

  if (kompakt) {
    return (
      <Stack gap={2}>
        <Text size="xs" c="dimmed">
          {`${stats.openCount} ${t("weekly.metrics.openTickets")} · ${antwortLabel}: ${antwort} (${nAntwort}) · ${t("weekly.metrics.avgResolution")}: ${loesung} (${nLoesung})`}
        </Text>
        {stats.staleTop.length > 0 && (
          <>
            <Button
              variant="subtle"
              size="compact-xs"
              w="fit-content"
              aria-expanded={offen}
              aria-controls={listeId}
              onClick={() => setOffen((o) => !o)}
            >
              {`${offen ? "▾" : "▸"} ${t("weekly.metrics.staleTickets")}`}
            </Button>
            <Collapse in={offen} id={listeId}>
              <StaleListe tickets={stats.staleTop} jiraBrowseBaseUrl={jiraBrowseBaseUrl} />
            </Collapse>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Paper withBorder p="md">
      <Stack gap="xs">
        <Text fw={600}>{t("weekly.metrics.ticketsTitle")}</Text>
        <Datenstand labelKey="weekly.metrics.jiraAsOf" syncedAt={jiraSyncedAt} bezugszeit={bezugszeit} />
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <Kachel label={t("weekly.metrics.openTickets")} wert={String(stats.openCount)} />
          <Kachel label={antwortLabel} wert={antwort} hinweis={nAntwort} />
          <Kachel label={t("weekly.metrics.avgResolution")} wert={loesung} hinweis={nLoesung} />
        </SimpleGrid>
        {stats.staleTop.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              {t("weekly.metrics.staleTickets")}
            </Text>
            <StaleListe tickets={stats.staleTop} jiraBrowseBaseUrl={jiraBrowseBaseUrl} />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function Kachel({ label, wert, hinweis }: { label: string; wert: string; hinweis?: string }) {
  return (
    <Paper withBorder p="sm">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xl" fw={700}>
        {wert}
      </Text>
      {hinweis && (
        <Text size="xs" c="dimmed">
          {hinweis}
        </Text>
      )}
    </Paper>
  );
}

function StaleListe({
  tickets,
  jiraBrowseBaseUrl,
}: {
  tickets: WeeklyStaleTicket[];
  jiraBrowseBaseUrl: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      {tickets.map((tk) => (
        <Group key={tk.key} gap={6} wrap="nowrap" align="flex-start">
          {jiraBrowseBaseUrl ? (
            <Anchor
              href={`${jiraBrowseBaseUrl}/browse/${tk.key}`}
              target="_blank"
              rel="noreferrer"
              size="xs"
              ff="monospace"
              style={{ whiteSpace: "nowrap" }}
            >
              {tk.key}
            </Anchor>
          ) : (
            <Text size="xs" ff="monospace" style={{ whiteSpace: "nowrap" }}>
              {tk.key}
            </Text>
          )}
          <Text size="xs" style={{ flex: 1, minWidth: 0 }} lineClamp={2}>
            {tk.summary}
          </Text>
          <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {[tk.status, t("weekly.metrics.daysSince", { count: tk.daysSinceActivity })].filter(Boolean).join(" · ")}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}
